const PINATA_IPFS_GATEWAY_MARKER = 'mypinata.cloud/ipfs';
const PINATA_THUMBNAIL_PARAMS = 'img-quality=20&img-format=webp';
const PINATA_IMAGE_PARAM_PREFIX = 'img-';

/**
 * Requests a Pinata-hosted image through the gateway's image transform, so a list thumbnail
 * or a boxed preview costs a compressed WebP instead of the full-resolution original the
 * depositor uploaded.
 *
 * Mirrors the marketplace's util of the same name. Anything that is not a Pinata gateway URL
 * — a local placeholder, an S3 link — is returned untouched, as is a URL that already carries
 * transform params.
 *
 * Only for images shown small. The full-size viewers deliberately keep the original: they are
 * there to read a serial number or judge a seal off, which quality 30 would take away.
 *
 * Pass the render width (about twice the CSS box, so it stays sharp on retina screens). Without
 * `img-width` the gateway still returns the full-resolution image, only re-encoded, so the width
 * is what makes a small thumbnail cost a kilobyte instead of tens of kilobytes.
 *
 * @param url   Any media URL. Non-Pinata URLs are returned untouched.
 * @param width Optional render width in px; omitted means "no resize" (previous behaviour).
 */
export function getPinataThumbnailUrl(url: string | null | undefined, width?: number): string | null | undefined {
    if (!url || typeof url !== 'string') return url;

    if (!url.includes(PINATA_IPFS_GATEWAY_MARKER)) return url;

    // Asking the optimizer again for a file it has already refused costs its 2-3s rejection
    // every time, so a known case goes straight to the original.
    if (isPinataUnoptimizable(url)) return stripPinataImageParams(url);

    const hasQuality = url.includes('img-quality=') || url.includes('img-format=');
    const hasWidth = url.includes('img-width=');

    // Already fully optimised for this call: nothing to add.
    if (hasQuality && (hasWidth || !width)) return url;

    const params: string[] = [];
    if (!hasQuality) params.push(PINATA_THUMBNAIL_PARAMS);
    if (width && !hasWidth) params.push(`img-width=${Math.round(width)}`);

    if (!params.length) return url;

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${params.join('&')}`;
}

/**
 * Removes the image-optimization params `getPinataThumbnailUrl` adds, leaving the original
 * object URL. Mirrors the marketplace's util of the same name.
 *
 * The gateway optimizer cannot decode every format: an AVIF source answers any `img-*`
 * request with 415, while the plain URL serves the file. The format cannot be told from a
 * bare CID, so an image that fails optimized is retried with this stripped URL.
 *
 * Non-Pinata URLs, and URLs carrying no `img-*` params, are returned untouched.
 */
export function stripPinataImageParams(url: string | null | undefined): string | null | undefined {
    if (!url || typeof url !== 'string') return url;
    if (!url.includes(PINATA_IPFS_GATEWAY_MARKER)) return url;

    const queryStart = url.indexOf('?');
    if (queryStart === -1) return url;

    const base = url.slice(0, queryStart);
    const rest = url.slice(queryStart + 1);
    const hashStart = rest.indexOf('#');
    const query = hashStart === -1 ? rest : rest.slice(0, hashStart);
    const hash = hashStart === -1 ? '' : rest.slice(hashStart);

    // Only the optimizer's own params go; anything else on the URL is left alone.
    const kept = query
        .split('&')
        .filter((param) => param && !param.toLowerCase().startsWith(PINATA_IMAGE_PARAM_PREFIX));

    if (kept.length === query.split('&').filter(Boolean).length) return url;

    return `${base}${kept.length ? `?${kept.join('&')}` : ''}${hash}`;
}

/**
 * Originals this browser session has seen the gateway refuse to optimize but serve as-is.
 *
 * Once an optimized URL has failed and its original has loaded, later requests for the same
 * file skip the optimizer instead of paying for its rejection again. Held in memory only, so
 * a reload starts afresh and a one-off gateway hiccup is never remembered beyond the session.
 */
const unoptimizableOriginals = new Set<string>();

/**
 * Records that the gateway serves this file but cannot optimize it.
 *
 * @param url The original URL that loaded after its optimized form failed. Any `img-*` params
 *            are ignored, so the optimized form may be passed as well.
 */
export function markPinataUnoptimizable(url: string | null | undefined): void {
    const original = stripPinataImageParams(url);
    if (typeof original === 'string' && original.includes(PINATA_IPFS_GATEWAY_MARKER)) {
        unoptimizableOriginals.add(original);
    }
}

/**
 * Whether the gateway is already known to refuse optimizing this file.
 *
 * @param url Original or optimized URL of the file.
 */
export function isPinataUnoptimizable(url: string | null | undefined): boolean {
    const original = stripPinataImageParams(url);
    return typeof original === 'string' && unoptimizableOriginals.has(original);
}

/**
 * Loads the original if an optimized Pinata image fails, for an <img> that Angular does not
 * render - the confirmation dialogs build theirs as HTML, so the NFT image directive cannot
 * reach it.
 *
 * @param img The dialog's image element; nothing happens when it is missing.
 */
export function retryPinataOriginalOnError(img: HTMLImageElement | null | undefined): void {
    if (!img) return;

    const retry = (): void => {
        const current = img.getAttribute('src');
        const original = stripPinataImageParams(current);
        if (typeof original !== 'string' || original === current) return;
        img.addEventListener('load', () => markPinataUnoptimizable(original), { once: true });
        img.src = original;
    };

    // The optimizer can reject before the dialog has finished opening.
    if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) {
        retry();
        return;
    }
    img.addEventListener('error', retry, { once: true });
}
